import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { debounceTime, distinctUntilChanged, merge, startWith } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnimationsService } from '../../services/animations.service';
import { FundCashFiltersModalComponent } from '../fund-cash-filters-modal/fund-cash-filters-modal.component';

export enum FundCashDateOptionEnum {
  LAST_WEEK,
  LAST_MONTH,
  LAST_SEMESTER,
}

export enum FundCashStatusFilterEnum {
  AUTHORIZED = 'AUTHORIZED',
  PAID = 'PAID',
  TRANSFERRED = 'TRANSFERRED',
}

export interface FundCashFilters {
  status?: (FundCashStatusFilterEnum | undefined)[];
  date?: (FundCashDateOptionEnum | undefined)[];
  description?: string;
}

const FUND_CASH_FILTER_DEFAULTS: Required<FundCashFilters> = {
  status: [undefined],
  date: [undefined],
  description: '',
};

type FundCashFiltersLike = {
  status?: (FundCashStatusFilterEnum | undefined)[] | null;
  date?: (FundCashDateOptionEnum | undefined)[] | null;
  description?: string | null;
};

export function createDefaultFundCashFilters(): FundCashFilters {
  return normalizeFundCashFilters();
}

export function normalizeFundCashFilters(
  filters?: FundCashFiltersLike
): FundCashFilters {
  return {
    status: [...(filters?.status ?? FUND_CASH_FILTER_DEFAULTS.status)],
    date: [...(filters?.date ?? FUND_CASH_FILTER_DEFAULTS.date)],
    description: filters?.description ?? FUND_CASH_FILTER_DEFAULTS.description,
  };
}

@UntilDestroy()
@Component({
  selector: 'ui-fund-cash-filter',
  templateUrl: './fund-cash-filters.component.html',
  styleUrls: ['./fund-cash-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundCashFiltersComponent {
  get hasActiveModalFilters(): boolean {
    const { status, date } = this.form.getRawValue();

    return (
      this.hasSelectedFilter(
        status as (FundCashStatusFilterEnum | undefined)[]
      ) ||
      this.hasSelectedFilter(date as (FundCashDateOptionEnum | undefined)[])
    );
  }

  @Input() set filters(filters: FundCashFilters) {
    const nextFilters = normalizeFundCashFilters(filters);

    this.searchControl.setValue(nextFilters.description ?? '', {
      emitEvent: false,
    });
    this.form.patchValue(
      {
        status: nextFilters.status,
        date: nextFilters.date,
      },
      { emitEvent: false }
    );
    this.changeDetectorRef.detectChanges();
  }

  @Output() filtersChanged = new EventEmitter<FundCashFilters>();

  readonly form = this.formBuilder.group({
    status: this.formBuilder.control<(FundCashStatusFilterEnum | undefined)[]>([
      undefined,
    ]),
    date: this.formBuilder.control<(FundCashDateOptionEnum | undefined)[]>([
      undefined,
    ]),
  });

  readonly searchControl = this.formBuilder.control<string>('', {
    nonNullable: true,
  });

  readonly viewUpdate$ = merge(
    this.form.valueChanges,
    this.searchControl.valueChanges
  ).pipe(startWith(null));

  constructor(
    private modalController: ModalController,
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private animationService: AnimationsService
  ) {
    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.emitFilters(this.searchControl.value));

    this.searchControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => this.changeDetectorRef.detectChanges());

    this.searchControl.valueChanges
      .pipe(untilDestroyed(this), debounceTime(300), distinctUntilChanged())
      .subscribe((description) => this.emitFilters(description));
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.changeDetectorRef.detectChanges();
  }

  async openFiltersModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: FundCashFiltersModalComponent,
      componentProps: {
        filters: normalizeFundCashFilters(this.form.getRawValue()),
      },
      enterAnimation: this.animationService.slideLeftToRight,
      leaveAnimation: this.animationService.slideRightToLeft,
    });

    await modal.present();

    const { data } = await modal.onWillDismiss<{
      status?: (FundCashStatusFilterEnum | undefined)[];
      date?: (FundCashDateOptionEnum | undefined)[];
    }>();

    if (!data) {
      return;
    }

    this.form.patchValue(normalizeFundCashFilters(data));
    this.changeDetectorRef.detectChanges();
  }

  private emitFilters(description: string): void {
    this.filtersChanged.emit(
      normalizeFundCashFilters({
        ...(this.form.getRawValue() as Omit<FundCashFilters, 'description'>),
        description,
      })
    );
  }

  private hasSelectedFilter(values?: unknown[]): boolean {
    return !!values?.length && !values.includes(undefined);
  }
}
